import { ReactFlowProvider } from "@xyflow/react";
import { Button, message, Modal, Spin } from "antd";
import { useGetScenarioRunQuery, useGetScenarioRunsQuery } from "api/TenantSlug/ConnectionCreator/hooks";
import { ccSelectedEdgeAtom, ccSelectedNodeAtom } from "components/CC/atoms/diagramAtom";
import { isDiagramDirtySelector } from "components/CC/atoms/diagramHistoryAtom";
import { diagramLogAtom } from "components/CC/atoms/diagramLogAtom";
import { diagramSaveAtom } from "components/CC/atoms/diagramSaveAtom.ts";
import { CCEntityProvider } from "components/CC/context/CCEntityContext.tsx";
import { useDiagramData, useDiagramMutations } from "components/CC/data";
import { defaultConnector } from "components/CC/data/connectors";
import {
    ConnectionCreatorDiagram,
    ConnectionCreatorDiagramRef,
} from "components/CC/Diagrams/ConnectionCreatorDiagram/ConnectionCreatorDiagram.tsx";
import { useEntityScenario } from "components/CC/hooks/useEntityScenario.ts";
import { CCEntityType, CC_ENTITY_CONFIGS, validateFlowType } from "components/CC/types/entityConfig.ts";
import { createDefaultStartNodes } from "components/CC/utils/createDefaultStartNodes.ts";
import {
    getCCDrawerSelectionParam,
    getEdgeSelectionId,
    getNodeSelectionId,
} from "components/CC/utils/drawerUrlState.ts";
import { aggregateHttpStepData } from "components/CC/utils/httpStepAggregation";
import { NodeFormData } from "components/CC/utils/nodeDefaults.ts";
import { validateDiagram, ValidationError } from "components/CC/utils/validateDiagram.ts";
import { useDrawer } from "hooks/useDrawer";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";

import GetScenarioRunRequest = App.Http.Requests.ConnectionCreatorNew.GetScenarioRunRequest;
import GetScenarioRunsRequest = App.Http.Requests.ConnectionCreatorNew.GetScenarioRunsRequest;

interface DiagramWithStorageProps {
    entityId: string;
    /** Entity type for CC configuration rules */
    entityType: CCEntityType;
    /** Optional scenario_id from entity - when BE adds this field, it can be passed directly */
    scenarioId?: string | null;
    /** Called after a new scenario is created - use to link it to the entity */
    onScenarioCreated?: (scenarioId: string) => Promise<void>;
    /**
     * Form fields for integration entity type.
     * Pass from the parent component to avoid a shared React Query cache key collision:
     * a separate useGetIntegrationQuery call here (without "scenarios" in the include) would
     * overwrite the shared cache and cause scenarioId to become null at save time.
     */
    formFields?: App.Data.FormFieldData[];
    /** Integration custom fields for ParameterPicker "keys" category. */
    customFields?: App.Data.IntegrationCustomFieldData[];
}

/**
 * Wrapper component that connects ConnectionCreatorDiagram to data storage.
 * Uses the connector system (MockConnector in dev, ApiConnector in production).
 *
 * If no scenario exists for the entity, shows an empty diagram.
 * Scenario is created on first save.
 */
export const DiagramWithStorage = ({
    entityId,
    entityType,
    scenarioId: providedScenarioId,
    onScenarioCreated,
    formFields,
    customFields,
}: DiagramWithStorageProps) => {
    const { t, i18n } = usePaldockTranslation("components.connection-creator");
    const [isSavingWithCreate, setIsSavingWithCreate] = useState(false);
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
    const { show: showDrawer } = useDrawer();
    const setSelectedNode = useSetRecoilState(ccSelectedNodeAtom);
    const setSelectedEdge = useSetRecoilState(ccSelectedEdgeAtom);
    const setHttpStepAggregates = useSetRecoilState(diagramLogAtom);
    const resetHttpStepAggregates = useResetRecoilState(diagramLogAtom);

    // Find scenario for this entity (doesn't auto-create)
    const { scenarioId, hasScenario, createScenario, isCreating } = useEntityScenario({
        entityId,
        entityType,
        scenarioId: providedScenarioId,
        onScenarioCreated,
    });

    // Load diagram data - only when we have a scenario and NOT currently saving
    // (prevents race condition: create scenario -> query enables -> GET before save completes)
    const {
        data,
        isLoading: isLoadingDiagram,
        isError,
        error,
    } = useDiagramData(scenarioId ?? undefined, {
        enabled: hasScenario && !isSavingWithCreate,
    });

    const latestRunListRequest = useMemo(
        () =>
            ({
                page: { number: 1, size: 1 },
                filter: {
                    filter: { scenario_id: scenarioId },
                    sort: "-created_at",
                },
            }) as GetScenarioRunsRequest,
        [scenarioId]
    );
    const latestScenarioRunsQuery = useGetScenarioRunsQuery(latestRunListRequest, {
        enabled: Boolean(scenarioId),
        staleTime: 10000,
        gcTime: 10000,
    });
    const latestScenarioRunId = latestScenarioRunsQuery.data?.scenarioRuns?.[0]?.id ?? "";

    const latestRunDetailRequest = useMemo(
        () =>
            ({
                filter: { include: "stepRuns" },
            }) as GetScenarioRunRequest,
        []
    );
    const latestScenarioRunQuery = useGetScenarioRunQuery(latestScenarioRunId, latestRunDetailRequest, {
        enabled: Boolean(latestScenarioRunId),
        staleTime: 10000,
        gcTime: 10000,
    });

    // Mutations for saving - scenarioId might be empty for new diagrams
    const { save: saveToExistingScenario, isSaving: isSavingMutation } = useDiagramMutations(scenarioId ?? "", {
        onSaveSuccess: () => {
            diagramRef.current?.markAsSaved();
            message.success(t("diagrams.save_success"));
        },
    });

    const diagramRef = useRef<ConnectionCreatorDiagramRef>(null);
    const setDiagramSave = useSetRecoilState(diagramSaveAtom);
    const resetDiagramSave = useResetRecoilState(diagramSaveAtom);

    // Combined saving state
    const isSaving = isSavingMutation || isSavingWithCreate || isCreating;

    const getValidationMessage = useCallback(
        (error: ValidationError) => {
            const translated = error.messageKey
                ? i18n.t(error.messageKey, error.params)
                : t(`validation.${error.key}`, error.params);
            const label = error.label ?? (error.labelKey ? i18n.t(error.labelKey) : undefined);
            return label ? `${label}: ${translated}` : translated;
        },
        [i18n, t]
    );

    // Handle clicking on validation error to open the target drawer
    const handleErrorClick = useCallback(
        (error: ValidationError) => {
            if (!error.drawerId || !diagramRef.current) return;

            const state = diagramRef.current.getState();

            if (error.nodeId) {
                const node = state.nodes.find((n) => n.id === error.nodeId);
                if (!node) return;

                setSelectedEdge(null);
                setSelectedNode({
                    id: node.id,
                    type: node.type || "",
                    formData: node.data?.formData as NodeFormData | undefined,
                });
                showDrawer(error.drawerId, {
                    [getCCDrawerSelectionParam(error.drawerId)]: getNodeSelectionId(node),
                });
            } else if (error.edgeId) {
                const edge = state.edges.find((e) => e.id === error.edgeId);
                if (!edge) return;

                setSelectedNode(null);
                setSelectedEdge({
                    id: edge.id,
                    source: edge.source,
                    target: edge.target,
                    data: edge.data as Record<string, unknown> | undefined,
                });
                showDrawer(error.drawerId, {
                    [getCCDrawerSelectionParam(error.drawerId)]: getEdgeSelectionId(edge),
                });
            } else {
                return;
            }
            setValidationErrors([]); // Close validation modal
        },
        [setSelectedEdge, setSelectedNode, showDrawer]
    );

    // Handle save - creates scenario first if needed
    const handleSave = useCallback(async () => {
        if (!diagramRef.current) return;
        const state = diagramRef.current.getState();

        // Validate before saving
        const errors = validateDiagram(state, entityType);
        if (errors.length > 0) {
            setValidationErrors(errors);
            return;
        }

        try {
            setIsSavingWithCreate(true);

            if (hasScenario && scenarioId) {
                // Scenario exists - save directly
                await saveToExistingScenario(state);
            } else {
                // No scenario - create one first, then save
                const newScenarioId = await createScenario();
                if (!newScenarioId) {
                    throw new Error("Failed to create scenario - no ID returned");
                }
                console.log("[CC] Scenario created, saving graph to:", newScenarioId);
                // Save using connector directly with new scenario ID
                await defaultConnector.save(newScenarioId, state);
                console.log("[CC] Graph saved successfully");
                diagramRef.current?.markAsSaved();
                message.success(t("diagrams.save_success"));
            }
        } catch (err) {
            console.error("[CC] Save failed:", err);
            message.error(t("diagrams.save_error"));
        } finally {
            setIsSavingWithCreate(false);
        }
    }, [hasScenario, scenarioId, saveToExistingScenario, createScenario, entityType, t]);

    // Register save function in atom
    useEffect(() => {
        setDiagramSave((prev) => ({
            ...prev,
            save: handleSave,
        }));
    }, [handleSave, setDiagramSave]);

    // Reset atom only on unmount (not when handleSave changes)
    useEffect(() => {
        return () => {
            resetDiagramSave();
        };
    }, [resetDiagramSave]);

    useEffect(() => {
        if (!scenarioId || !latestScenarioRunId) {
            setHttpStepAggregates([]);
            return;
        }

        if (!latestScenarioRunQuery.isSuccess) {
            return;
        }

        const aggregatedHttpStepLogs = aggregateHttpStepData(latestScenarioRunQuery.data.scenarioRun?.stepRuns ?? []);
        setHttpStepAggregates(aggregatedHttpStepLogs);
    }, [
        scenarioId,
        latestScenarioRunId,
        latestScenarioRunQuery.isSuccess,
        latestScenarioRunQuery.data?.scenarioRun?.stepRuns,
        setHttpStepAggregates,
    ]);

    useEffect(() => {
        return () => {
            resetHttpStepAggregates();
        };
    }, [resetHttpStepAggregates]);

    // Sync dirty state directly from history atom (more robust than callback chain)
    const isDiagramDirty = useRecoilValue(isDiagramDirtySelector);
    useEffect(() => {
        setDiagramSave((prev) => ({
            ...prev,
            hasChanges: isDiagramDirty,
        }));
    }, [isDiagramDirty, setDiagramSave]);

    // Transform nodes to fix invalid flow types based on entity rules
    const transformedNodes = useMemo(() => {
        if (!data?.nodes) return [];

        return data.nodes.map((node) => {
            if (node.type !== "startNode") return node;

            const formData = node.data?.formData as { flow?: string } | undefined;
            const currentFlow = formData?.flow;
            const validatedFlow = validateFlowType(entityType, currentFlow);

            if (currentFlow === validatedFlow) return node;

            return {
                ...node,
                data: {
                    ...node.data,
                    formData: {
                        ...(formData || {}),
                        flow: validatedFlow,
                    },
                },
            };
        });
    }, [data?.nodes, entityType]);

    // Default nodes for empty diagram (when no scenario exists)
    const defaultDiagramData = useMemo(() => {
        return createDefaultStartNodes(entityType);
    }, [entityType]);
    const entityConfig = CC_ENTITY_CONFIGS[entityType];
    const shouldUseDefaultGraph =
        !hasScenario ||
        (entityConfig.showDefaultWhenGraphEmpty && transformedNodes.length === 0 && (data?.edges?.length ?? 0) === 0);

    // Loading diagram data (only if scenario exists)
    // IMPORTANT: Wait for data to load before rendering to prevent initializing with default nodes
    if (hasScenario && (isLoadingDiagram || !data)) {
        return (
            <div className="flex items-center justify-center" style={{ height: "calc(100vh - 250px)" }}>
                <Spin size="large" />
            </div>
        );
    }

    // Error loading diagram
    if (hasScenario && isError) {
        return (
            <div className="flex flex-col items-center justify-center gap-2" style={{ height: "calc(100vh - 250px)" }}>
                <div className="text-red-500 font-medium">{t("diagrams.load_error")}</div>
                {error?.message && <div className="text-red-400 text-sm">{error.message}</div>}
            </div>
        );
    }

    // Render diagram - empty if no scenario, loaded data if scenario exists
    return (
        <CCEntityProvider
            entityType={entityType}
            entityId={entityId}
            scenarioId={scenarioId}
            formFields={formFields}
            customFields={customFields}
        >
            <div style={{ height: "calc(100vh - 250px)", width: "100%", minHeight: "500px", position: "relative" }}>
                <ReactFlowProvider>
                    <ConnectionCreatorDiagram
                        ref={diagramRef}
                        onSave={handleSave}
                        initialNodes={shouldUseDefaultGraph ? defaultDiagramData.nodes : transformedNodes}
                        initialEdges={shouldUseDefaultGraph ? defaultDiagramData.edges : data?.edges || []}
                        initialNotes={hasScenario ? data?.notes || "" : ""}
                        isSaving={isSaving}
                    />
                </ReactFlowProvider>
                {/* Saving overlay */}
                {isSaving && (
                    <div
                        className="absolute inset-0 flex items-center justify-center z-10"
                        style={{ backgroundColor: "rgba(255, 255, 255, 0.6)" }}
                    >
                        <div className="flex flex-col items-center gap-2 bg-white p-4 rounded-lg shadow-lg">
                            <Spin size="large" />
                            <span className="text-gray-600">{t("diagrams.saving")}</span>
                        </div>
                    </div>
                )}
                {/* Validation errors modal */}
                <Modal
                    open={validationErrors.length > 0}
                    title={t("validation.error_title")}
                    onCancel={() => setValidationErrors([])}
                    footer={[
                        <Button key="close" onClick={() => setValidationErrors([])}>
                            {t("validation.close")}
                        </Button>,
                    ]}
                >
                    <ul className="list-disc pl-4 space-y-2">
                        {validationErrors.map((err, index) => (
                            <li key={index}>
                                {err.drawerId && (err.nodeId || err.edgeId) ? (
                                    <Button
                                        type="link"
                                        className="p-0 h-auto text-left"
                                        onClick={() => handleErrorClick(err)}
                                    >
                                        {getValidationMessage(err)}
                                    </Button>
                                ) : (
                                    <span>{getValidationMessage(err)}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                </Modal>
            </div>
        </CCEntityProvider>
    );
};
